import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Warning from "@mui/icons-material/Warning";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  IssueFragment,
  ShippableContainerItemFragment,
  UnitType,
  WasteClassification,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { formatQuantity, NO_VALUE_PLACEHOLDER } from "../Containers/containerDisplay";
import { classificationLabels } from "../Overview/Tiles/Charts/constants";
import {
  CREATE_WASTE_SHIPMENT_MUTATION,
  FACILITIES_WITH_SHIPPABLE_CONTAINERS_QUERY,
  SHIPPABLE_CONTAINERS_QUERY,
  VALIDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { DeadlineChip } from "./DeadlineChip";
import { daysUntil } from "./shipmentDisplay";

type ShippableContainer = ShippableContainerItemFragment;
type FacilityOption = { id: string; name: string };

const SOON_DUE_DAYS = 30;

/**
 * The picker is a hand-built row layout rather than a table, so its header and
 * its rows have to agree on column widths independently. Keeping the widths
 * here means widening a column can't silently misalign the label above it.
 *
 * The header's leading spacer stands in for each row's `size="small"` Checkbox:
 * a 20px icon inside 9px of padding on each side.
 */
const CHECKBOX_COLUMN = { width: 38, flexShrink: 0 };
const LABEL_COLUMN = { width: 96, flexShrink: 0 };
const AMOUNT_COLUMN = { width: 90, flexShrink: 0 };
const LOCATION_COLUMN = { flexGrow: 1, minWidth: 0 };

/** Containers grouped by waste stream (uncharacterized last), as the picker shows them. */
function groupByStream(containers: ShippableContainer[]) {
  const groups = new Map<
    string,
    {
      streamName: string | null;
      classification: WasteClassification | null;
      containers: ShippableContainer[];
    }
  >();
  for (const container of containers) {
    const key = container.wasteStream?.id ?? "";
    const group = groups.get(key) ?? {
      streamName: container.wasteStream?.name ?? null,
      classification: container.wasteStream?.classification ?? null,
      containers: [],
    };
    group.containers.push(container);
    groups.set(key, group);
  }
  return [...groups.entries()]
    .map(([streamId, group]) => ({ streamId, ...group }))
    .sort((a, b) => (a.streamName ?? "￿").localeCompare(b.streamName ?? "￿"));
}

/**
 * Per-unit totals for a container group. A stream can hold containers in
 * different units (gallons + pounds), so the header shows one total per unit
 * ("120 gal · 30 lb") rather than a single dimensionally-invalid sum.
 * Containers with no recorded amount are skipped.
 */
function groupUnitTotals(containers: ShippableContainer[]): { unit: UnitType; total: number }[] {
  const totals = new Map<UnitType, number>();
  for (const c of containers) {
    if (c.amount == null || c.amountUnit == null) {
      continue;
    }
    totals.set(c.amountUnit, (totals.get(c.amountUnit) ?? 0) + c.amount);
  }
  return [...totals.entries()]
    .map(([unit, total]) => ({ unit, total }))
    .sort((a, b) => a.unit.localeCompare(b.unit));
}

/** Loading placeholder shaped like a stream group + its container rows. */
function ContainerListSkeleton() {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ py: 1, backgroundColor: "grey.50", px: 1 }}
      >
        <Skeleton variant="text" width={150} />
        <Skeleton variant="rounded" width={88} height={22} />
        <Box sx={{ flexGrow: 1 }} />
        <Skeleton variant="text" width={80} />
      </Stack>
      {[0, 1, 2].map((i) => (
        <Stack
          key={i}
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 1, py: 0.75, borderTop: 1, borderColor: "divider" }}
        >
          <Skeleton variant="rounded" width={18} height={18} />
          <Skeleton variant="text" sx={LABEL_COLUMN} />
          <Skeleton variant="text" sx={AMOUNT_COLUMN} />
          <Skeleton variant="text" sx={{ flexGrow: 1, maxWidth: 240 }} />
        </Stack>
      ))}
    </Box>
  );
}

/**
 * Column labels for the picker. Rendered once above the stream groups — the
 * group headers between them name the stream, not the columns. "Deadline" gets
 * no fixed width and so sits flush right, matching the deadline chip that ends
 * each row: the conditional open-shipment icon before it shifts the chip's left
 * edge but never its right.
 */
function ContainerColumnHeader() {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1, pb: 0.5 }}>
      <Box sx={CHECKBOX_COLUMN} />
      <Typography variant="caption" color="text.secondary" sx={LABEL_COLUMN}>
        Container
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={AMOUNT_COLUMN}>
        Amount
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={LOCATION_COLUMN}>
        Location
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Deadline
      </Typography>
    </Stack>
  );
}

function ContainerRow({
  container,
  checked,
  onToggle,
}: {
  container: ShippableContainer;
  checked: boolean;
  onToggle: (id: string) => void;
}) {
  const emptiesTank = container.isPermanent;
  const openShipments = container.openShipments;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 1, py: 0.75, borderTop: 1, borderColor: "divider" }}
    >
      <Checkbox
        size="small"
        checked={checked}
        onChange={() => onToggle(container.id)}
        inputProps={{ "aria-label": `Select ${container.containerLabel}` }}
      />
      <Typography variant="subtitle2" sx={LABEL_COLUMN}>
        {container.containerLabel}
      </Typography>
      <Typography variant="body2" sx={AMOUNT_COLUMN}>
        {container.amount != null && container.amountUnit
          ? formatQuantity(container.amount, container.amountUnit)
          : NO_VALUE_PLACEHOLDER}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={LOCATION_COLUMN}>
        {container.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER}
        {emptiesTank && (
          <Typography component="span" variant="body2" color="warning.main">
            {" · Empties the tank (stays on site)"}
          </Typography>
        )}
      </Typography>
      {openShipments.length > 0 && (
        <Tooltip title={openShipmentTooltip(openShipments)}>
          <Warning color="warning" fontSize="small" aria-label="Already on an open shipment" />
        </Tooltip>
      )}
      <DeadlineChip deadline={container.clock.effectiveDeadlineDate} />
    </Stack>
  );
}

/** "Already on open shipment MR-0003" / "…open shipments MR-0003, MR-0004". */
function openShipmentTooltip(openShipments: ShippableContainer["openShipments"]): string {
  const numbers = openShipments.map((s) => s.manifestRequestNumber);
  const plural = numbers.length === 1 ? "shipment" : "shipments";
  return `Already on open ${plural} ${numbers.join(", ")}`;
}

export function PackageShipmentBuilder() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const alerts = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    { label: "Package a shipment" },
  ]);

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: facilityId ?? "" },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });
  const containers = useMemo(
    () => data?.facility?.shippableContainers?.items ?? [],
    [data?.facility?.shippableContainers?.items]
  );
  // While switching facilities the query has no cached result for the new
  // facility yet, so `data` is briefly undefined. Treat that as loading (not
  // "0 containers") so the count/empty-state don't flash a stale zero.
  const loadingContainers = loading && data?.facility == null;

  // Only facilities that actually have something to ship, so the picker
  // never lands the user on an empty facility.
  const { data: facilitiesData, loading: facilitiesLoading } = useQuery(
    FACILITIES_WITH_SHIPPABLE_CONTAINERS_QUERY,
    { variables: { tenantId: tenantId ?? "" }, skip: !tenantId }
  );
  const facilityOptions: FacilityOption[] =
    facilitiesData?.tenant?.facilitiesWithShippableContainers ?? [];
  const selectedFacility = facilityOptions.find((f) => f.id === facilityId) ?? null;
  const facilityName = selectedFacility?.name ?? data?.facility?.name;

  // Warn-level validation (e.g. a picked container already on another open
  // shipment) surfaces live as the selection changes; CRITICAL rules are
  // enforced by the mutation itself.
  const [validate, { data: validationData, previousData: prevValidationData }] = useLazyQuery(
    VALIDATE_WASTE_SHIPMENT_INPUT,
    { fetchPolicy: "no-cache" }
  );
  useEffect(() => {
    if (facilityId && selectedIds.size > 0) {
      validate({ variables: { input: { facilityId, containerIds: [...selectedIds] } } });
    }
  }, [facilityId, selectedIds, validate]);
  // Fall back to the prior result while a re-validation is in flight so the
  // critical-issue alert stays mounted and only its text updates — otherwise
  // the no-cache query blanks data mid-flight and the alert flashes.
  const effectiveValidation = validationData ?? prevValidationData;
  const validationIssues: IssueFragment[] =
    selectedIds.size > 0 ? (effectiveValidation?.wasteShipmentInputValidator ?? []) : [];
  const criticalIssues = validationIssues.filter((issue) => issue.level === "CRITICAL");

  const [createShipment, { loading: creating }] = useMutation(CREATE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: ["TenantWasteShipments", "FacilityWasteShipments"],
  });

  const groups = useMemo(() => groupByStream(containers), [containers]);
  const soonDueCount = useMemo(
    () =>
      containers.filter((c) => {
        const days = daysUntil(c.clock.effectiveDeadlineDate);
        return days !== null && days <= SOON_DUE_DAYS;
      }).length,
    [containers]
  );

  const selected = useMemo(
    () => containers.filter((c) => selectedIds.has(c.id)),
    [containers, selectedIds]
  );

  // Distinct streams on the request — a stream split across units still
  // counts once, so the header never disagrees with what the user selected.
  const selectedStreamCount = useMemo(
    () => new Set(selected.map((c) => c.wasteStream?.id ?? "")).size,
    [selected]
  );

  // The live "manifest request" summary: selected containers rolled up by
  // stream + unit, mirroring the backend's grouping — shown compactly in the
  // summary card so the forming request is legible before creating.
  const requestLines = useMemo(() => {
    const lines = new Map<
      string,
      { streamName: string | null; count: number; total: number; unit: UnitType | null }
    >();
    for (const container of selected) {
      const unit = container.amountUnit ?? container.capacityUnit;
      const key = `${container.wasteStream?.id ?? ""}|${unit ?? ""}`;
      const line = lines.get(key) ?? {
        streamName: container.wasteStream?.name ?? null,
        count: 0,
        total: 0,
        unit: unit ?? null,
      };
      line.count += 1;
      line.total += container.amount ?? 0;
      lines.set(key, line);
    }
    return [...lines.values()].sort((a, b) =>
      (a.streamName ?? "￿").localeCompare(b.streamName ?? "￿")
    );
  }, [selected]);

  const previewStreamSummary = useMemo(() => {
    const shown = requestLines.slice(0, 3).map((line) => {
      const qty = line.unit ? formatQuantity(line.total, line.unit) : `${line.count} cont`;
      return `${line.streamName ?? "Uncharacterized"} ${qty}`;
    });
    const more = requestLines.length - shown.length;
    return shown.join(" · ") + (more > 0 ? ` · +${more} more` : "");
  }, [requestLines]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Add or remove a batch of ids at once — the master and per-group
  // checkboxes select/deselect their whole scope through this.
  const setSelection = useCallback((ids: string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }, []);

  // Master checkbox: checked when every ready container is selected,
  // indeterminate when only some are.
  const allSelected = containers.length > 0 && containers.every((c) => selectedIds.has(c.id));
  const someSelected = containers.some((c) => selectedIds.has(c.id));

  const handleFacilityChange = (facility: { id: string } | null) => {
    const next = new URLSearchParams(searchParams);
    if (facility) {
      next.set("facilityId", facility.id);
    } else {
      next.delete("facilityId");
    }
    setSearchParams(next, { replace: true });
    setSelectedIds(new Set());
  };

  const handleCreate = async () => {
    if (!facilityId || selectedIds.size === 0) {
      return;
    }
    try {
      const result = await createShipment({
        variables: { input: { facilityId, containerIds: [...selectedIds] } },
      });
      const created = result.data?.createWasteShipment;
      alerts.success(`Shipment ${created?.manifestRequestNumber ?? ""} created.`);
      if (created) {
        // Header details (vendor, transporter, TSDF, dates) are filled in on
        // the shipment itself — land there.
        navigate(`/o/${tenantId}/waste/shipments/${created.id}`);
      }
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to create the shipment.");
    }
  };

  if (!tenantId) {
    return null;
  }

  const facilitySelect = (
    <Autocomplete
      options={facilityOptions}
      getOptionLabel={(o: FacilityOption) => o.name}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      value={selectedFacility}
      onChange={(_e, value) => handleFacilityChange(value)}
      loading={facilitiesLoading}
      fullWidth
      renderInput={(params) => <TextField {...params} label="Facility" size="small" />}
      noOptionsText="No facilities have containers ready to ship"
    />
  );

  return (
    <Stack spacing={2} sx={{ py: 2 }}>
      <Box sx={{ px: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Shipment{facilityName ? ` · ${facilityName}` : ""}
        </Typography>
        <Typography variant="h5">Package a shipment</Typography>
        <Typography variant="body2" color="text.secondary">
          Select what&apos;s leaving central accumulation. Encamp builds a manifest request for your
          vendor — it isn&apos;t the official manifest.
        </Typography>
      </Box>

      {!facilityId && (
        <Box sx={{ px: 3, display: "flex", justifyContent: "center", pt: 4 }}>
          <Paper
            variant="outlined"
            sx={{ p: 4, maxWidth: 460, width: "100%", textAlign: "center" }}
          >
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              Which facility is shipping?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Only facilities with containers ready to ship are listed.
            </Typography>
            {facilitySelect}
          </Paper>
        </Box>
      )}

      {facilityId && <Box sx={{ px: 3, maxWidth: 420 }}>{facilitySelect}</Box>}

      {facilityId && soonDueCount > 0 && (
        <Box sx={{ px: 3 }}>
          <Alert severity="warning">
            <strong>
              {soonDueCount} container{soonDueCount === 1 ? " is" : "s are"} due within 30 days.
            </strong>{" "}
            {soonDueCount === 1
              ? "Ship it to stay compliant."
              : "Ship them together to stay compliant and minimize hauls."}
          </Alert>
        </Box>
      )}

      {facilityId && (
        <Box sx={{ px: 3 }}>
          <Paper elevation={0} sx={{ py: 2 }}>
            {/* px matches the group headers and rows so the master checkbox
                lines up in the same column as the ones below it. */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 1 }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                disabled={loadingContainers || containers.length === 0}
                onChange={(e) =>
                  setSelection(
                    containers.map((c) => c.id),
                    e.target.checked
                  )
                }
                inputProps={{ "aria-label": "Select all ready containers" }}
              />
              {loadingContainers ? (
                <Skeleton variant="text" width={260} />
              ) : (
                <Typography variant="subtitle1">
                  {containers.length} container{containers.length === 1 ? "" : "s"} ready in central
                  accumulation
                </Typography>
              )}
            </Stack>

            {loadingContainers && <ContainerListSkeleton />}

            {!loadingContainers && containers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No central-accumulation containers are ready to ship at this facility.
              </Typography>
            )}

            {!loadingContainers && containers.length > 0 && <ContainerColumnHeader />}

            {groups.map((group) => {
              const unitTotals = groupUnitTotals(group.containers);
              const groupIds = group.containers.map((c) => c.id);
              const groupAllSelected = groupIds.every((id) => selectedIds.has(id));
              const groupSomeSelected = groupIds.some((id) => selectedIds.has(id));
              return (
                <Box key={group.streamId || "uncharacterized"} sx={{ mb: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ py: 0.5, backgroundColor: "grey.50", px: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={groupAllSelected}
                      indeterminate={groupSomeSelected && !groupAllSelected}
                      onChange={(e) => setSelection(groupIds, e.target.checked)}
                      inputProps={{
                        "aria-label": `Select all ${group.streamName ?? "uncharacterized"} containers`,
                      }}
                    />
                    <Typography variant="subtitle2">
                      {group.streamName ?? "Uncharacterized"}
                    </Typography>
                    {group.classification && (
                      <Chip
                        label={classificationLabels[group.classification] ?? group.classification}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {group.containers.length} cont
                      {unitTotals.length > 0
                        ? ` · ${unitTotals.map((t) => formatQuantity(t.total, t.unit)).join(" · ")}`
                        : ""}
                    </Typography>
                  </Stack>
                  {group.containers.map((container) => (
                    <ContainerRow
                      key={container.id}
                      container={container}
                      checked={selectedIds.has(container.id)}
                      onToggle={toggle}
                    />
                  ))}
                </Box>
              );
            })}
          </Paper>
        </Box>
      )}

      {facilityId && containers.length > 0 && (
        <Box sx={{ px: 3 }}>
          <Paper elevation={0} sx={{ py: 2 }}>
            {/* Open-shipment double-packaging shows per row (warning icon);
                only the rare blocking issues surface here as a safety net. */}
            {criticalIssues.length > 0 && (
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                {criticalIssues.map((issue, i) => (
                  <Alert key={`crit-${issue.key}-${i}`} severity="error">
                    {issue.message}
                  </Alert>
                ))}
              </Stack>
            )}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              spacing={2}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">
                  {selectedIds.size} container{selectedIds.size === 1 ? "" : "s"} ·{" "}
                  {selectedStreamCount} stream{selectedStreamCount === 1 ? "" : "s"}
                  {previewStreamSummary ? ` · ${previewStreamSummary}` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Not an official manifest — your vendor creates and returns that. Vendor,
                  transporter, and dates are added on the shipment after it&apos;s created.
                </Typography>
              </Box>
              <Button
                variant="contained"
                disabled={selectedIds.size === 0 || creating}
                onClick={handleCreate}
                // The label must not wrap, so the button must not be allowed to
                // shrink under it either — between the sm breakpoint and about
                // 700px the summary text would otherwise squeeze it and the
                // label would spill outside its own background.
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Create draft shipment
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </Stack>
  );
}
